import React from 'react';
import styled from 'styled-components';
import { H4, Subtitle3 } from 'ensightful-koala-ui';

import { Body1, H3 } from 'components/atoms/Text';
import { CourseSetUpContainer, HeaderContainer } from 'components/organisms/CourseSetUp';
import { PrimaryButton } from 'components/molecules/Button';
import assetFiles from 'utils/asset';
import UploadCSV from './UploadCSV';

const OptionWrapper = styled.div`
    border-radius: 12px;
    box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1), 0px 2px 2px rgba(0, 0, 0, 0.03);
    padding: 16px 0px 16px 0px;
    position: relative;

    & > div {
        border-bottom: 1px solid #e0e0e0;
    }

    & > div:last-child {
        border: none;
    }
`;

const OptionContainer = styled.div`
    width: 100%;
    height: fit-content;
    display: flex;
    flex-wrap: wrap-reverse;
    justify-content: center;
    align-items: center;
    padding: 25px 12px 12px 12px;
`;

const OptionLeftContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 250px;
`;

const OptionRightContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 300px;

    & img {
        width: auto;
        max-height: 148px;
        max-width: 100%;
    }
`;

const StyledLink = styled.span`
    color: #6347ff;
    text-decoration: underline;

    &:hover {
        cursor: pointer;
    }
`;

const StyledButton = styled(PrimaryButton)`
    min-width: 12em;
`;

const TeamSetUpOptions = ({
    fieldKeys,
    options,
    courseData,
    handleInputChange,
    review = false,
    downloadTemplate,
    handleOptionClick,
    handleOpenGroupSyncModal,
    isLti = false,
}) => {
    const handleCSVOptions = (data) => {
        handleInputChange(fieldKeys?.STUDENT_LIST, data);
        return handleOptionClick(options.CSV);
    };

    const handleCanvasSyncOption = () => {
        return handleOpenGroupSyncModal();
    };

    return (
        <CourseSetUpContainer>
            <HeaderContainer review={review}>
                <H3>Team Set-up</H3>
                <Body1 style={{ marginBottom: 25 }}>
                    Choose how you would like to create your teams for the course.
                </Body1>
            </HeaderContainer>
            <OptionWrapper>
                {isLti && (
                    <OptionContainer>
                        <OptionLeftContainer>
                            <H4 style={{ marginBottom: 8 }}>Through Canvas</H4>
                            <Body1 style={{ maxWidth: 245 }}>Sync your group roster from Canvas.</Body1>
                            <Subtitle3 style={{ marginBottom: 20, maxWidth: 220, color: '#db5019' }}>
                                * Make sure you only have one group set on Canvas.
                            </Subtitle3>
                            <StyledButton
                                text={'Sync with Canvas'}
                                active={true}
                                onClick={() => handleCanvasSyncOption()}
                            />
                        </OptionLeftContainer>
                        <OptionRightContainer>
                            <img src={assetFiles.image.canvas_graphic} />
                        </OptionRightContainer>
                    </OptionContainer>
                )}
                <OptionContainer>
                    <OptionLeftContainer>
                        <H4 style={{ marginBottom: 8 }}>Through External CSV</H4>
                        <Body1 style={{ marginBottom: 20, maxWidth: 245 }}>
                            <StyledLink onClick={() => downloadTemplate('csv_option')}>Download</StyledLink> a CSV of
                            your roster and upload the teams.
                        </Body1>
                        <UploadCSV
                            onRequest={(data) => handleCSVOptions(data)}
                            userData={courseData[fieldKeys?.STUDENT_LIST]}
                            option={'primary-button'}
                        />
                    </OptionLeftContainer>
                    <OptionRightContainer>
                        <img src={assetFiles.image.csv_graphic} />
                    </OptionRightContainer>
                </OptionContainer>
                <OptionContainer>
                    <OptionLeftContainer>
                        <H4 style={{ marginBottom: 8 }}>Through Ensightful</H4>
                        <Body1 style={{ marginBottom: 20, maxWidth: 245 }}>
                            Randomize, manually distribute students, or generate diverse groups based on a set of
                            criteria you specify.
                        </Body1>
                        <StyledButton
                            text={'Create from Scratch'}
                            active={true}
                            onClick={() => handleOptionClick(options.MANUAL)}
                        />
                    </OptionLeftContainer>
                    <OptionRightContainer>
                        <img src={assetFiles.image.through_ensightful_graphic} />
                    </OptionRightContainer>
                </OptionContainer>
            </OptionWrapper>
        </CourseSetUpContainer>
    );
};

export default TeamSetUpOptions;
